import React from 'react';
import { motion } from 'framer-motion';
import { Warning } from '@phosphor-icons/react';

const Disclaimer = () => {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Legal
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Risk Disclaimer
          </h1>
          <p className="text-[var(--text-secondary)] mb-8">
            Last updated: January 15, 2026
          </p>

          {/* Warning Banner */}
          <div className="p-6 rounded-2xl bg-[var(--accent-danger)]/10 border border-[var(--accent-danger)]/30 mb-8">
            <div className="flex items-start gap-4">
              <Warning size={32} className="text-[var(--accent-danger)] flex-shrink-0" weight="fill" />
              <div>
                <h3 className="text-lg font-semibold text-[var(--accent-danger)] mb-2">
                  Important Risk Warning
                </h3>
                <p className="text-[var(--text-secondary)]">
                  Trading cryptocurrencies carries a high level of risk and may result in the loss of your entire investment. You should not invest money that you cannot afford to lose.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8 text-[var(--text-secondary)]">
            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">General Risk Warning</h2>
              <p>
                The information provided by DeFi Bot and its affiliates does not constitute investment advice, financial advice, trading advice, or any other sort of advice. You should not treat any of the platform's content as such.
              </p>
              <p className="mt-4">
                DeFi Bot does not recommend that any cryptocurrency should be bought, sold, or held by you. Do conduct your own due diligence and consult your financial advisor before making any investment decisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Cryptocurrency Trading Risks</h2>
              <p>The risks of cryptocurrency trading include but are not limited to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-3">
                <li>
                  <strong className="text-[var(--text-primary)]">Market Volatility:</strong> Cryptocurrency prices can be extremely volatile. The value of your assets may fluctuate significantly within short periods.
                </li>
                <li>
                  <strong className="text-[var(--text-primary)]">Liquidity Risk:</strong> Some cryptocurrencies may have limited liquidity, making it difficult to buy or sell at desired prices.
                </li>
                <li>
                  <strong className="text-[var(--text-primary)]">Regulatory Risk:</strong> Cryptocurrency regulations vary by jurisdiction and may change without notice, potentially affecting your ability to trade.
                </li>
                <li>
                  <strong className="text-[var(--text-primary)]">Technology Risk:</strong> Smart contracts, blockchain networks, and trading bots may contain bugs or vulnerabilities.
                </li>
                <li>
                  <strong className="text-[var(--text-primary)]">Security Risk:</strong> Digital assets are susceptible to hacking, theft, and fraud.
                </li>
                <li>
                  <strong className="text-[var(--text-primary)]">Operational Risk:</strong> Trading bots may malfunction, execute unintended trades, or fail to execute trades.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Automated Trading Risks</h2>
              <p>When using automated trading systems like DeFi Bot, you should be aware that:</p>
              <ul className="list-disc pl-6 mt-4 space-y-3">
                <li>Automated trading does not guarantee profits or prevent losses</li>
                <li>Technical issues, network delays, or system failures may affect trade execution</li>
                <li>Market conditions may render certain strategies ineffective</li>
                <li>Historical performance does not guarantee future results</li>
                <li>Settings and parameters may need continuous adjustment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">No Guarantees</h2>
              <p>
                DeFi Bot makes no representations or warranties that:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>The platform will generate profits</li>
                <li>Any specific return on investment will be achieved</li>
                <li>The platform will be available without interruption</li>
                <li>All trades will be executed as expected</li>
                <li>Historical performance statistics will continue</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Your Responsibilities</h2>
              <p>By using DeFi Bot, you acknowledge and accept that:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>You are solely responsible for your trading decisions</li>
                <li>You have sufficient knowledge to understand the risks involved</li>
                <li>You are using capital that you can afford to lose entirely</li>
                <li>You will not hold DeFi Bot liable for any losses</li>
                <li>You will comply with all applicable laws in your jurisdiction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Seek Professional Advice</h2>
              <p>
                We strongly recommend that you seek independent financial, legal, and tax advice before making any cryptocurrency investment decisions. The information provided on this platform is for educational purposes only and should not be construed as professional advice.
              </p>
            </section>

            <section className="p-6 rounded-xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Acknowledgment</h2>
              <p>
                By accessing and using DeFi Bot, you confirm that you have read, understood, and agree to all the risks outlined in this disclaimer. You acknowledge that cryptocurrency trading is speculative and involves substantial risk of loss.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Disclaimer;
